import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import '../css/Checkout.css'
import { getPlan, isPlanId, type PlanId } from '../constants/plans'
import { getAuthTokenCookie } from '../utils/authToken'

const API_URL = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL

export const Route = createFileRoute('/checkout')({
  component: Checkout,
  validateSearch: (search: { plan?: string | null } = {}) => {
    const plan = isPlanId(search.plan) ? (search.plan as PlanId) : 'premium'
    return { plan }
  },
})

function Checkout() {
  const { plan } = Route.useSearch()
  const selectedPlan = getPlan(plan)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Redirect to Stripe Checkout when component mounts
    const redirectToCheckout = async () => {
      try {
        setLoading(true)
        setError(null)

        // Get auth token from cookie
        const token = getAuthTokenCookie()
        
        if (!token) {
          setError('You must be logged in to checkout')
          setLoading(false)
          return
        }

        const response = await fetch(`${API_URL}/payment/create-checkout-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            planId: selectedPlan.id
          })
        })

        if (!response.ok) {
          throw new Error(`Failed to create checkout session: ${response.statusText}`)
        }

        const data = await response.json()
        
        if (data.success && data.data.url) {
          // Redirect to Stripe Checkout
          window.location.href = data.data.url
        } else {
          throw new Error('Invalid response from server')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize checkout')
        setLoading(false)
      }
    }

    // Only redirect for paid plans
    if (selectedPlan.priceCents && selectedPlan.priceCents > 0) {
      redirectToCheckout()
    } else {
      setLoading(false)
      setError('This plan is not available for checkout')
    }
  }, [selectedPlan])

  return (
    <div className="checkout-container">
      <header className="checkout-header">
        <h1>Checkout</h1>
        <p>You're selecting the <strong>{selectedPlan.name}</strong> plan.</p>
      </header>

      {loading && (
        <div className="checkout-alert">
          <h3>Redirecting to Stripe...</h3>
          <p>Please wait while we redirect you to complete your subscription.</p>
        </div>
      )}

      {error && (
        <div className="checkout-alert">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      )}

      <footer className="checkout-footer">
        <Link to="/pricing" className="back-link">← Back to Pricing</Link>
      </footer>
    </div>
  )
}
